import type { FC } from 'react';

import { useConfig } from '@packages/utilities';

import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { SearchResultPageDefaultContentFragmentFragmentDoc } from '@packages/gql/generated/shopping/SearchResultPageDefaultContentFragmentFragmentDoc';
import type { SearchSeoApiResponse } from '../../api/rest/types';
import { useOriginalSearchQuery } from '../../hooks/useOriginalSearchQuery';

import { ProductListPageLayout } from '../ProductListPageLayout';
import { SearchResultHeader } from '../SearchResultHeader';
import { SearchFeedback } from '../SearchFeedback';
import { ProductListSeo } from '../ProductListSeo';
import { Recommendations } from '../Recommendations';
import { SearchResultCategoryFilter } from '../SearchResultCategoryFilter';

/* GraphQL */ `
  fragment SearchResultPageDefaultContentFragment on SuccessfulSearchResult {
    ...SearchResultHeaderFragment
    ...ProductListPageLayoutFragment
    ...SearchResultCategoryFilterFragment
  }
`;

export type SearchResultPageDefaultContentProps = {
  maskedData: FragmentType<typeof SearchResultPageDefaultContentFragmentFragmentDoc>;
  topLevelCategories?: string[];
  seoResponse?: SearchSeoApiResponse;
  referrer?: string;
};

/**
 * SearchResultPageDefaultContent component description displayed in storybook
 * */
export const SearchResultPageDefaultContent: FC<SearchResultPageDefaultContentProps> = ({
  maskedData,
  topLevelCategories,
  seoResponse,
  referrer,
}) => {
  const { feedback } = useConfig();
  const originalQuery = useOriginalSearchQuery();

  const data = getFragmentData(SearchResultPageDefaultContentFragmentFragmentDoc, maskedData);

  const seoInformationExists = seoResponse && Object.keys(seoResponse).length > 0;

  return (
    <ProductListPageLayout
      maskedData={data}
      topLevelCategories={topLevelCategories}
      referrer={referrer}
      aboveMainContentComponent={
        <SearchResultHeader maskedData={data} originalQuery={originalQuery} />
      }
      belowProductsComponent={
        <>
          <Recommendations />
          {feedback.enabled && <SearchFeedback />}
          {seoInformationExists && (
            <ProductListSeo
              data={{
                ...seoResponse,
                relationlinks: undefined,
                similarlinks: undefined,
                filterlinks: undefined,
              }} // Only toplinks (shopping-tips) should be displayed in SERP
            />
          )}
        </>
      }
      leftSidebarDesktopComponent={<SearchResultCategoryFilter maskedData={data} />}
      renderCategoryDialogContent={(closeDialog) => (
        <SearchResultCategoryFilter maskedData={data} onNavigate={closeDialog} />
      )}
    />
  );
};
